import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { ChevronRightOutlined, CheckOutlined, CloseOutlined, VisibilityOutlined } from '@mui/icons-material';
import QuickLookDrawer from '../QuickLookDrawer/QuickLookDrawer';

function initials(a, b) {
  const first = (a ?? '').trim();
  const second = (b ?? '').trim();
  if (first && second) return `${first[0]}${second[0]}`.toUpperCase();
  const parts = first.split(/\s+/);
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase() || '—';
}

const AVATAR_COLORS = ['#0094B3', '#7C3AED', '#059669', '#DB2777', '#D97706', '#2563EB'];
function avatarColor(seed) {
  const str = String(seed ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) hash = (hash * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[hash];
}

function statusStyles(status) {
  const str =
    typeof status === 'object' && status !== null ? status.Status ?? status.ApproverUIStatus ?? '' : status ?? '';
  const map = {
    Pending: { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', dot: 'bg-[#F59E0B]' },
    Submitted: { bg: 'bg-[#E6F4F8]', text: 'text-[#0094B3]', dot: 'bg-[#0094B3]' },
    Approved: { bg: 'bg-[#D1FAE5]', text: 'text-[#059669]', dot: 'bg-[#10B981]' },
    Rejected: { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', dot: 'bg-[#EF4444]' },
    Draft: { bg: 'bg-[#F1F5F9]', text: 'text-[#64748B]', dot: 'bg-[#94A3B8]' }
  };
  return { label: str, ...(map[str] ?? map.Draft) };
}

function timesheetSummary(item) {
  const days =
    item.TimesheetDetails?.[0]?.TimesheetHoursByDates ??
    item.TimesheetSections?.[0]?.TimesheetDetails?.[0]?.TimesheetHoursByDates;
  let period = item.PeriodLabel ?? '—';
  if (days?.length) {
    const start = dayjs(days[0].Day);
    const end = dayjs(days.at(-1).Day);
    period = start.isSame(end, 'month')
      ? `${start.format('D')} – ${end.format('D MMM YYYY')}`
      : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }
  return {
    title: `${item.ContractorFirstName ?? ''} ${item.ContractorLastName ?? ''}`.trim() || item.ClientName || '—',
    subtitle: `${period}${item.TSNumber ? ` · ${item.TSNumber}` : ''}`,
    contractorName: `${item.ContractorFirstName ?? ''} ${item.ContractorLastName ?? ''}`.trim()
  };
}

function expenseSummary(item) {
  const claimName = item.ClaimDescription ?? item.ClaimName ?? '—';
  const contractorName =
    item.User?.FirstName || item.User?.LastName
      ? `${item.User.FirstName ?? ''} ${item.User.LastName ?? ''}`.trim()
      : item.ClientName ?? '—';
  const lineCount = item.ExpenseDetails?.length ?? item.LineItemCount ?? 0;
  return {
    title: contractorName,
    subtitle: `${claimName}${lineCount ? ` · ${lineCount} item${lineCount !== 1 ? 's' : ''}` : ''}`,
    contractorName
  };
}

const SWIPE_THRESHOLD = 76;
const MAX_SWIPE = 96;

function SwipeableRow({ item, type, isApprover, onOpen, onApprove, onReject, onQuickLook }) {
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const swipeEnabled = isApprover && (onApprove || onReject);

  const { title, subtitle, contractorName } = type === 'expense' ? expenseSummary(item) : timesheetSummary(item);
  const st = statusStyles(item.Status);

  const handlePointerDown = (e) => {
    if (!swipeEnabled) return;
    startX.current = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    setDragging(true);
  };

  const handlePointerMove = (e) => {
    if (!swipeEnabled || !dragging) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const delta = x - startX.current;
    const clamped = Math.max(-MAX_SWIPE, Math.min(MAX_SWIPE, delta));
    setDragX(clamped);
  };

  const handlePointerUp = () => {
    if (!swipeEnabled) return;
    setDragging(false);
    if (dragX >= SWIPE_THRESHOLD && onApprove) {
      onApprove(item.Id);
    } else if (dragX <= -SWIPE_THRESHOLD && onReject) {
      onReject(item.Id);
    }
    setDragX(0);
  };

  return (
    <div className="relative overflow-hidden border-b border-[#E2E8ED] last:border-b-0">
      {swipeEnabled && (
        <div className="absolute inset-0 flex items-center justify-between px-6">
          <span
            className={`flex items-center gap-1.5 text-[#059669] font-semibold text-[13px] transition-opacity ${
              dragX > 12 ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <CheckOutlined sx={{ fontSize: 16 }} /> Approve
          </span>
          <span
            className={`flex items-center gap-1.5 text-[#DC2626] font-semibold text-[13px] transition-opacity ${
              dragX < -12 ? 'opacity-100' : 'opacity-0'
            }`}
          >
            Reject <CloseOutlined sx={{ fontSize: 16 }} />
          </span>
        </div>
      )}

      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => dragging && handlePointerUp()}
        onClick={() => Math.abs(dragX) < 4 && onOpen(item.Id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            if (Math.abs(dragX) < 4) onOpen(item.Id);
          }
        }}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragging ? 'none' : 'transform 0.25s cubic-bezier(0.4,0,0.2,1)',
          touchAction: swipeEnabled ? 'pan-y' : 'auto'
        }}
        className="relative bg-white flex items-center gap-3 px-5 py-3.5 cursor-pointer active:bg-[#F8FAFB]"
      >
        {isApprover ? (
          <span
            className="flex items-center justify-center w-9 h-9 rounded-full text-white text-[12px] font-bold shrink-0"
            style={{ backgroundColor: avatarColor(item.Id ?? contractorName) }}
          >
            {initials(item.ContractorFirstName, item.ContractorLastName) || initials(contractorName)}
          </span>
        ) : null}

        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-[#022D41] leading-tight truncate">{title}</div>
          <div className="text-[12.5px] text-[#9AA3AE] mt-0.5 truncate">{subtitle}</div>
        </div>

        {st.label && (
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold shrink-0 ${st.bg} ${st.text}`}
          >
            <span className={`w-[5px] h-[5px] rounded-full shrink-0 ${st.dot}`} />
            {st.label}
          </span>
        )}
        {isApprover && onQuickLook && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onQuickLook(item);
            }}
            aria-label="Quick look"
            className="flex items-center justify-center w-7 h-7 rounded-full bg-[#F0F4F7] border-none cursor-pointer shrink-0 hover:bg-[#E2E8ED] transition-colors"
          >
            <VisibilityOutlined sx={{ fontSize: 15, color: '#0094B3' }} />
          </button>
        )}
        <ChevronRightOutlined sx={{ fontSize: 18, color: '#C4CDD5', flexShrink: 0 }} />
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 border-b border-[#E2E8ED] animate-pulse">
      <div className="w-9 h-9 rounded-full bg-[#E2E8ED] shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="h-3.5 bg-[#E2E8ED] rounded w-32 mb-1.5" />
        <div className="h-3 bg-[#E2E8ED] rounded w-20" />
      </div>
      <div className="h-5 bg-[#E2E8ED] rounded-full w-16" />
    </div>
  );
}

export default function TNECardsList({
  type = 'timesheet',
  data,
  clickHandler,
  isLoading,
  isApprover: isApproverProp,
  loadingComponent,
  onApprove,
  onReject
}) {
  const isApproverStore = useSelector((s) => s.accessToParts?.tAndEApprover);
  const isApprover = isApproverProp ?? isApproverStore;
  const swipeEnabled = isApprover && (onApprove || onReject);
  const [quickLookItem, setQuickLookItem] = useState(null);

  if (isLoading) {
    return (
      loadingComponent ?? (
        <div className="bg-white rounded-[12px] border border-[#E2E8ED] overflow-hidden">
          {[0, 1, 2, 3, 4].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      )
    );
  }

  const list = data ?? [];

  if (list.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-14 gap-2 text-[#6B7A85] bg-white rounded-[12px] border border-[#E2E8ED]">
        <span className="text-[14px]">No {type === 'expense' ? 'expenses' : 'timesheets'} to show</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[12px] border border-[#E2E8ED] overflow-hidden">
      {isApprover && (
        <p className="flex items-center gap-1 text-[12px] text-[#9AA3AE] px-5 pt-3 pb-2 m-0 border-b border-[#E2E8ED]">
          {swipeEnabled ? 'Swipe right to approve · left to reject · ' : ''}tap 👁 for a quick look
        </p>
      )}
      {list.map((item) => (
        <SwipeableRow
          key={item.Id}
          item={item}
          type={type}
          isApprover={isApprover}
          onOpen={clickHandler}
          onApprove={onApprove}
          onReject={onReject}
          onQuickLook={setQuickLookItem}
        />
      ))}

      <QuickLookDrawer
        open={!!quickLookItem}
        item={quickLookItem}
        type={type}
        onClose={() => setQuickLookItem(null)}
        onApprove={onApprove}
        onReject={onReject}
      />
    </div>
  );
}
